import './Status.scss';
import {useStatus} from '@/discord/hooks/useStatus';
import { useEffect, useState } from 'react';
import {formatMinutes} from '@/discord/utils/timeUtils';

const MINUTES_PER_DAY = 1440;

export function Status() {
    const { isOperational, statusDowntime, staffInServiceList, loading } = useStatus();
    const staffInServiceCount = staffInServiceList?.all?.length ?? 0;

    const getDowntimeDays = () => (window.innerWidth > 1900 ? 90 : (window.innerWidth > 867 ? 60 : 30));
    const [downtimeDays, setDowntimeDays] = useState(getDowntimeDays);

    useEffect(() => {
        const onResize = () => {
            setDowntimeDays(getDowntimeDays());
        };

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Disponibilité calculée sur la MÊME fenêtre que les barres affichées.
    let availability: number | null = null;
    if (isOperational) {
        const windowDays = (statusDowntime ?? []).slice(-downtimeDays);
        const totalDays = windowDays.length;
        if (totalDays > 0) {
            const totalMinutes = totalDays * MINUTES_PER_DAY;
            const totalDowntime = windowDays.reduce((sum, day) => sum + day.downtime_minutes, 0);
            availability = ((totalMinutes - totalDowntime) / totalMinutes) * 100;
        }
    }

    return (
        <main className="page-status">
            <div className="layout hero">
                <div className="hero-description">
                    <div className="pala-item pala-item-title primary">
                        <h3 className="pala-item-title-subtitle">Statut Support</h3>
                        <h1 className="pala-item-title-title">Suivez Le Statut Du Support&nbsp;!</h1>
                        <p className="pala-item-title-text">
                            Suivez en temps réel le statut des services du support de Paladium ainsi que la liste des staff en service.
                        </p>
                    </div>
                    <div className="hero-description-buttons">
                        <a href="/status#api-and-bot">
                            <div className="pala-item-button primary large">
                                <p>API & Bot</p>
                            </div>
                        </a>
                        <a href="/status#staff">
                            <div className="pala-item-button second large">
                                <p>Staff en service</p>
                            </div>
                        </a>
                    </div>
                    <div className="hero-description-stats">
                        <div className="pala-item pala-item-stats">
                            <h4 className="pala-items-stats-value">
                                24h/24
                            </h4>
                            <h6 className="pala-items-stats-description">
                                Opérationnel
                            </h6>
                        </div>
                        <div className="pala-item pala-item-stats">
                            <h4 className="pala-items-stats-value">
                                +100
                            </h4>
                            <h6 className="pala-items-stats-description">
                                Staff
                            </h6>
                        </div>
                    </div>
                </div>
                <div className="hero-image">
                    <img src="/arty/arty_endium.gif" alt="Arty"/>
                </div>
            </div>
            <div className="layout api-and-bot" id="api-and-bot">
                <div className="api-and-bot-container">
                    <div className="api-and-bot-header">
                        <div className="pala-item pala-item-subtitle primary">
                            <div className="pala-item-subtitle-container">
                                <h4>
                                    API & Bot
                                </h4>
                            </div>
                            <p className="pala-item-subtitle-text">
                                Découvrez la disponibilité de l'API et du bot lors des {downtimeDays} derniers jours.
                            </p>
                        </div>
                        <span className="pala-item api-and-bot-operational">
                            {loading
                                ? <>
                                    <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="32" cy="32" r="30" fill="#8b949e"/>
                                        <circle cx="22" cy="32" r="3.5" fill="white"/>
                                        <circle cx="32" cy="32" r="3.5" fill="white"/>
                                        <circle cx="42" cy="32" r="3.5" fill="white"/>
                                    </svg>
                                    <h5>Vérification…</h5>
                                </> : isOperational
                                ? <>
                                    <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="32" cy="32" r="30" fill="#22c55e"/>
                                        <path
                                            d="M18 33 L28 43 L46 21"
                                            fill="none"
                                            stroke="white"
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <h5>Opérationnel</h5>
                                </> : <>
                                    <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="32" cy="32" r="30" fill="#ef4444"/>
                                        <line
                                            x1="32" y1="16" x2="32" y2="38"
                                            stroke="white"
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                        />
                                        <circle cx="32" cy="46" r="3.5" fill="white"/>
                                    </svg>
                                    <h5>En panne</h5>
                                </>
                            }
                        </span>
                    </div>
                    <div className="api-and-bot-content">
                        <div className="api-and-bot-downtime">
                            <div className={`pala-item api-and-bot-downtime-availability`}>
                                <h6 className={availability !== null ? "available" : "unavailable"}>
                                    { availability !== null
                                        ? `${parseFloat(availability.toFixed(2))}% de disponibilité`
                                        : `Impossible de calculer la disponibilité`
                                    }
                                </h6>
                            </div>
                            <div className="api-and-bot-downtime-container">
                                {(statusDowntime ?? [])
                                    .slice(-downtimeDays)
                                    .map((downtime, index) => (
                                        <div key={index} className="api-and-bot-downtime-wrapper pala-item">
                                                <div className="api-and-bot-downtime-item-hover">
                                                    <div className="api-and-bot-downtime-item-hover-header">
                                                        {downtime.downtime_minutes === 0
                                                            ? <span className="api-and-bot-downtime-item-hover-status">
                                                                <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                                                    <circle cx="32" cy="32" r="30" fill="#22c55e"/>
                                                                    <path
                                                                        d="M18 33 L28 43 L46 21"
                                                                        fill="none"
                                                                        stroke="white"
                                                                        strokeWidth="6"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                                <h6>Opérationnel</h6>
                                                            </span> : <>
                                                                <span className="api-and-bot-downtime-item-hover-status">
                                                                    <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                                                        <circle cx="32" cy="32" r="30" fill="#ef4444"/>
                                                                        <line
                                                                            x1="32" y1="16" x2="32" y2="38"
                                                                            stroke="white"
                                                                            strokeWidth="6"
                                                                            strokeLinecap="round"
                                                                        />
                                                                        <circle cx="32" cy="46" r="3.5" fill="white"/>
                                                                    </svg>
                                                                    <h6>Temps d'arrêt</h6>
                                                                </span>
                                                                <p>
                                                                    Arrêté pendant {formatMinutes(downtime.downtime_minutes)}
                                                                </p>
                                                            </>
                                                        }
                                                    </div>
                                                    <hr className="pala-item-separation-bar"/>
                                                    <p>
                                                        {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(downtime.day))}
                                                    </p>
                                                </div>
                                            <div className={`api-and-bot-downtime-item ${downtime.downtime_minutes > 0 ? "positive" : "zero"}`}/>
                                        </div>
                                    ))
                                }
                            </div>
                            <div className="api-and-bot-downtime-legend">
                                <p>Il y a {downtimeDays} jours</p>
                                <p className="today">Aujourd'hui</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="layout staff" id="staff">
                <div className="staff-container">
                    <div className="staff-header">
                        <div className="pala-item pala-item-subtitle primary">
                            <div className="pala-item-subtitle-container">
                                <h4>
                                    Staff en service
                                </h4>
                            </div>
                            <p className="pala-item-subtitle-text">
                                Découvrez la liste des staffs actuellement en service sur le serveur Discord.
                            </p>
                        </div>
                        <div className="pala-item staff-count">
                            <h5>
                                {staffInServiceCount} membre{staffInServiceCount > 1 ? "s" : ""} du staff en service
                            </h5>
                        </div>
                    </div>
                    <div className="pala-item staff-content pala-item">
                        <div key="java" className="staff-list-container">
                            <h4>Java</h4>
                            <ul className="staff-list">
                                {(staffInServiceList?.java ?? []).map(({ displayName, avatarURL, roleName, roleColor }) => (
                                    <li key={displayName}>
                                    <span>
                                        <img src={avatarURL} alt={displayName}/>
                                        <h6>{displayName}</h6>
                                    </span>
                                        <h5 style={{color: roleColor}}>{roleName}</h5>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div key="bedrock" className="staff-list-container">
                            <h4>Bedrock</h4>
                            <ul className="staff-list">
                                {(staffInServiceList?.bedrock ?? []).map(({ displayName, avatarURL, roleName, roleColor }) => (
                                    <li key={displayName}>
                                    <span>
                                        <img src={avatarURL} alt={displayName}/>
                                        <h6>{displayName}</h6>
                                    </span>
                                        <h5 style={{color: roleColor}}>{roleName}</h5>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div key="discord" className="staff-list-container">
                            <h4>Discord</h4>
                            <ul className="staff-list">
                                {(staffInServiceList?.discord ?? []).map(({ displayName, avatarURL, roleName, roleColor }) => (
                                    <li key={displayName}>
                                    <span>
                                        <img src={avatarURL} alt={displayName}/>
                                        <h6>{displayName}</h6>
                                    </span>
                                        <h5 style={{color: roleColor}}>{roleName}</h5>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="staff-buttons">
                        <div className="staff-button">
                            <a href="https://tally.so/r/w4eq85">
                                <div className="pala-item-button large second">
                                    <p>Rejoindre le staff Java</p>
                                </div>
                            </a>
                        </div>
                        <div className="staff-button">
                            <a href="https://tally.so/r/1AP5XL" className="staff-join-them-button">
                                <div className="pala-item-button large second">
                                    <p>Rejoindre le staff Bedrock</p>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    )
}